import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement
} from "./network/managedRequestForAccountingArea";
import {PbRequest} from "./pbRequest";
import {API} from "../constants";

export interface InstallationLink {
    location_id: number;
    token: string;
    expire_timestamp: number;
    status: 'VALID' | 'INVALID';
}

interface InstallationLinkStore {
    installationLink?: InstallationLink;
}

const initialState: InstallationLinkStore = {
    installationLink: undefined,
};

const installationLinkSlice = createSlice({
    name: 'installationLink',
    initialState,
    reducers: {
        setInstallationLink(
            state: InstallationLinkStore,
            action: PayloadAction<InstallationLink>
        ) {
            state.installationLink = action.payload;
        },
    },
});

export function GetInstallationLink(locationId: number) {
    return async (dispatch) => {
        try {
            const request: RequestInit = {
                method: 'GET',
            };
            const result = await processRequestForAccountingAreaWithReplacement<InstallationLink>(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/installation_link`,
                request,
                PbRequest.GET_INSTALLATION_LINK
            )
            dispatch(installationLinkSlice.actions.setInstallationLink(result));
        } catch {
            dispatch(installationLinkSlice.actions.setInstallationLink(undefined));
        }
    };
}

interface InstallationLinkWriteDto {
    expire_timestamp: number;
}

export function CreateInstallationLink(locationId: number, data: InstallationLinkWriteDto) {
    return async (dispatch) => {
        try {
            const request: RequestInit = {
                method: 'POST',
                body: JSON.stringify(data),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/installation_link`,
                request,
                PbRequest.CREATE_INSTALLATION_LINK
            )
        } catch {
        } finally {
            dispatch(GetInstallationLink(locationId));
        }
    };
}

export function ExtendInstallationLink(locationId: number, data: InstallationLinkWriteDto) {
    return async (dispatch) => {
        try {
            const request: RequestInit = {
                method: 'PATCH',
                body: JSON.stringify(data),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/installation_link`,
                request,
                PbRequest.EXTEND_INSTALLATION_LINK
            )
        } catch {
        } finally {
            dispatch(GetInstallationLink(locationId));
        }
    };
}

export function RevokeInstallationLink(locationId: number) {
    return async (dispatch) => {
        try {
            const request: RequestInit = {
                method: 'DELETE',
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/installation_link`,
                request,
                PbRequest.REVOKE_INSTALLATION_LINK
            )
        } catch {
        } finally {
            dispatch(GetInstallationLink(locationId));
        }
    };
}

interface InstallationLinkSendViaEmailDto {
    email: string;
}

export function SendInstallationLink(locationId: number, data: InstallationLinkSendViaEmailDto) {
    return async (dispatch) => {
        try {
            const request: RequestInit = {
                method: 'POST',
                body: JSON.stringify(data),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/installation_link/send`,
                request,
                PbRequest.SEND_INSTALLATION_LINK
            )
        } catch {
        } finally {
            dispatch(GetInstallationLink(locationId));
        }
    };
}

export default installationLinkSlice.reducer;
